import requiredFieldConfig from '../data/requiredFields.json';
import { getAccessToken } from '../services/authApi';
import { fieldReader } from '../services/loanApi';
import {
    collectConditionFields, evaluateCompositeCondition, evaluateShowIfCondition, isEmptyField,
    violatesValidateRule, isInvalidNumber, isFieldEffectivelyEmpty
} from '../utils/conditionUtils';
import { isPollyProcessingStatus } from '../utils/pricingValidation';
import { runLicensingGateForMenu } from '../utils/licenseValidation';
import { getServiceOrders, hasFulfilledClosingFeeOrder } from '../services/orderService';
import { getBlockedLoanProgramName } from '../utils/loanProgramUtil';

const getFreshAccessToken = async () => {
    const authObject = await window.elli.script.getObject("auth");
    const authCode = await authObject.createAuthCode();
    return await getAccessToken(authCode);
};

// Returns: { values, accessToken }
const readFieldsWithRefresh = async (accessToken, fieldIds, loanId) => {
    try {
        const values = await fieldReader(accessToken, fieldIds, loanId);
        return { values, accessToken };
    } catch (err) {
        const status = err?.response?.status;

        if (status === 401) {
            // refresh ONCE and reuse
            const newToken = await getFreshAccessToken();
            const values = await fieldReader(newToken, fieldIds, loanId);
            return { values, accessToken: newToken };
        }

        throw err;
    }
};
const getBorrowerPairCount = (values) => {
    const raw = values["4460"];
    const count = parseInt(raw, 10);
    return Number.isFinite(count) && count > 0 ? count : 1;
};

export const checkValidationRequired = async (menuItem, loanId, opts = {}, token = "") => {
    if (!menuItem?.validationExtPageId || !menuItem?.validationPage) return false;

    let validationConfig = null;
    if (menuItem.displayName === "Order Appraisal" || menuItem.displayName === "Order Smart Fees") {
        validationConfig = requiredFieldConfig.Pages.find(
            p => p.Page === menuItem.displayName
        );
    }
    else {
        validationConfig = requiredFieldConfig.Pages.find(
            p => p.Page === menuItem.label || (p.Aliases && p.Aliases.includes(menuItem.label))
        );
    }
    if (!validationConfig) return false;

    let accessToken = token;
    if (!accessToken) {
        accessToken = await getFreshAccessToken();
    }

    const fieldIdsToRead = new Set([
        "CX.LOAN.TYPE",
        "LOCKRATE.CURRENTSTATUS",
        "4788", // Lock Validation Status
        "230",
        "231",
        "NEWHUD.X139",
        "1621",
        "CX.TPOC.OVERRIDE.VALIDATIONS",
        "LCP.X1",
        "LCP.X8",
        "CX.LEGAL.ENTITY.TYPE",
        "CX.ALL.DISCLOSURES.SENT",
        "1401",
        "CX.EXC.PRE.ISAPPROVED",
        "4460",
        "300#2",
        "300#3",
        "300#4",
        "300#5",
        ...(validationConfig.SkipIf?.field ? [validationConfig.SkipIf.field] : [])
    ]);

    validationConfig.FieldGroups.forEach(group => {
        (group.completionFields || []).forEach(fieldId => {
            if (fieldId) fieldIdsToRead.add(fieldId);
        });

        group.Fields.forEach(field => {
            if (field.id) fieldIdsToRead.add(field.id);
        });

        // ensure we can evaluate group visibility conditions
        if (group.ShowIf?.field) {
            fieldIdsToRead.add(group.ShowIf.field);
        }

        // support composite ShowIf (all/any) without changing existing logic
        if (group.ShowIf && !group.ShowIf.field) {
            collectConditionFields(group.ShowIf).forEach(fid => fieldIdsToRead.add(fid));
        }

        if (group.NotRequiredIf) {
            collectConditionFields(group.NotRequiredIf).forEach(fid => fieldIdsToRead.add(fid));
        }
    });

    const allFieldIds = Array.from(fieldIdsToRead);
    let { values, accessToken: refreshedToken } = await readFieldsWithRefresh(
        accessToken,
        allFieldIds,
        loanId
    );

    const overrideValidations =
        (values["CX.TPOC.OVERRIDE.VALIDATIONS"] || "").toString().trim().toUpperCase();

    const skipModuleValidations = overrideValidations === "Y";

    const loanType = values["CX.LOAN.TYPE"]?.trim();
    const stateCode = (values["14"] || "").trim();
    const city = (values["12"] || "").trim();
    const tpoOrgId = (values["TPO.X15"] || "").trim();
    const tpoUserId = (values["TPO.X62"] || "").trim();
    const legalEntityType = (values['CX.LEGAL.ENTITY.TYPE'] || '').trim();

    // Skip validation entirely if SkipIf condition is met
    const skipCondition = validationConfig.SkipIf;
    if (skipCondition) {
        const val = (values[skipCondition.field] || '').trim();
        const shouldSkip = skipCondition.operator === 'notEmpty' && val !== '' && val !== '//' && val !== '  /  /    ';
        if (shouldSkip) return false;
    }

    let applicableGroups = validationConfig.FieldGroups.filter(group =>
        !group.LoanType || group.LoanType === loanType
    );

    // If override validations = Y, skip only Module type groups.
    // Continue validating Field type groups.
    if (skipModuleValidations) {
        console.log("[checkValidationRequired] Override validations=Y, skipping Module validations only");

        applicableGroups = applicableGroups.filter(group =>
            (group.Type || "").toLowerCase() !== "module"
        );
    }

    // Dynamically skip the "Request Disclosure" group for Submit Loan
    const brokerOpt = (opts.brokerOptions || '').toLowerCase();
    if (
        !skipModuleValidations &&
        menuItem.label === 'Submit Loan' &&
        brokerOpt !== 'generateleanddisclosures' &&
        brokerOpt !== 'requestleanddisclosures'
    ) {
        applicableGroups = applicableGroups.filter(g => g.Group !== 'Request Disclosure');
    }

    let hasDisclosureValidationRequired = false;

    // NEW LOGIC (no other changes):
    // BOTH Consumer & Business: when Submit Loan + GenerateLEAndDisclosures
    // validate that ALL borrower applications are disclosed (Initial)
    if (
        !skipModuleValidations &&
        menuItem.label === 'Submit Loan' &&
        brokerOpt === 'generateleanddisclosures'
    ) {
        applicableGroups = applicableGroups.filter(g => g.Group !== 'Request Disclosure');

        const allDisclosuresSent =
            (values["CX.ALL.DISCLOSURES.SENT"] || "").toString().trim().toUpperCase() === "Y";

        hasDisclosureValidationRequired = !allDisclosuresSent;
    }

    if (
        !skipModuleValidations &&
        loanType === 'Business' &&
        menuItem.label === 'Submit Loan' &&
        brokerOpt !== 'generateleanddisclosures'
    ) {
        applicableGroups = applicableGroups.filter(g => g.Group !== 'Send Disclosures');
    }

    const isGroupNotRequired = (group, values) =>
        group.NotRequiredIf ? evaluateCompositeCondition(group.NotRequiredIf, values) : false;

    const isBorrowerPaid =
        (values['LCP.X1'] || '').trim().toLowerCase() === 'borrower paid';

    let EXCLUDE_FROM_REQUIRED = new Set([
        '4788',
        'LOCKRATE.CURRENTSTATUS',
        'NEWHUD.X139',
        '1621',
        'LCP.X8',
        'CX.LEGAL.ENTITY.TYPE'
    ]);

    if (!isBorrowerPaid) {
        EXCLUDE_FROM_REQUIRED.add('389');
    }

    const fieldIdsFromGroups = applicableGroups
        .filter(group => !isGroupNotRequired(group, values))
        .flatMap(group => {
            const fields = (group.Fields || []).filter(f => f?.id);
            const completionFields = (group.completionFields || []).filter(Boolean);

            if (completionFields.length > 0) {
                return completionFields;
            }

            if (group.Group === 'Insurance and Fees') {
                return fields
                    .filter(f => {
                        if (!f.requiredIf) return true;

                        const triggerId = f.requiredIf.field;
                        const triggerEmpty = isEmptyField(values[triggerId]);

                        if (f.requiredIf.operator === 'notEmpty') {
                            // required only when trigger has a value
                            return !triggerEmpty;
                        }
                        if (f.requiredIf.operator === 'empty') {
                            // required only when trigger is empty
                            return triggerEmpty;
                        }

                        return true;
                    })
                    .map(f => f.id);
            }

            // default for other groups
            return fields.map(f => f.id);
        })
        .filter(Boolean)
        .filter(id => !EXCLUDE_FROM_REQUIRED.has(id));

    // Rule 1: Any required field is empty
    const missingFieldIds = [];
    const uniqueFieldIdsFromGroups = [...new Set(fieldIdsFromGroups)];

    uniqueFieldIdsFromGroups.forEach(id => {
        if (id === "300#1") {
            const pairCount = getBorrowerPairCount(values);

            for (let pairIndex = 1; pairIndex <= pairCount; pairIndex++) {
                const pairFieldId = `300#${pairIndex}`;

                if (isEmptyField(values[pairFieldId])) {
                    missingFieldIds.push(pairFieldId);
                }
            }
            return;
        }

        if (String(id) === "389") {
            const allowZero = (loanType || "").trim().toLowerCase() === "business";

            if (isInvalidNumber(values[id], allowZero)) {
                missingFieldIds.push(id);
            }
            return;
        }

        if (isEmptyField(values[id])) {
            missingFieldIds.push(id);
        }
    });

    if (missingFieldIds.length > 0) {
        console.log('[checkValidationRequired] Missing required fields:', missingFieldIds);
    }
    const hasMissingField = missingFieldIds.length > 0;


    // Rule 1b: Any field violates its JSON validate rule (e.g., URLA.X73 notTBD)
    const hasInvalidByValidate = applicableGroups.some(group =>
        (group.Fields || []).some(f => violatesValidateRule(f, values[f.id]))
    );

    // Rule 2: Any ShowIf condition is met
    const showIfTriggered = applicableGroups.some(group => {
        const isBusinessLoan = (loanType || "").trim().toLowerCase() === "business";

        // If Mortgage Fees is satisfied (Borrower Paid + 389 filled), don't count its ShowIf as a trigger
        if (group.Group === 'Mortgage Fees' && isBorrowerPaid && !isInvalidNumber(values['389'], isBusinessLoan)) {
            return false;
        }
        // If Insurance Information group is satisfied (one of the insurance amount + months pairs is filled),
        if (group.Group === 'Insurance and Fees') {
            const homeownerAmountFilled = !isEmptyField(values['230']);
            const homeownerMonthsFilled = !isEmptyField(values['1387']);
            const mortgageAmountFilled = !isEmptyField(values['231']);
            const mortgageMonthsFilled = !isEmptyField(values['1386']);

            const homeownerSatisfied = !homeownerAmountFilled || (homeownerAmountFilled && homeownerMonthsFilled);
            const mortgageSatisfied = !mortgageAmountFilled || (mortgageAmountFilled && mortgageMonthsFilled);

            if (homeownerSatisfied && mortgageSatisfied) {
                return false;
            }
            return true;
        }
        // Loan program blocked for this org (ChampsTPO: ACTIVATOR PRIME) - SkyTPO's
        // PROGRAM_009 must not trigger it, so the org config decides, not the ShowIf alone.
        if (group.Group === 'Loan Program Not Available') {
            return Boolean(getBlockedLoanProgramName(values[group.ShowIf?.field], values['1401']));
        }
        return evaluateShowIfCondition(group, values);
    });

    // Rule 3: If user clicks "Send Disclosures" and lock needs validation, force redirect to validation page
    const lockNeedsValidation = (values['4788'] || '').trim() === 'Needs Validation';
    const isDisclosurePage = menuItem.label === "Send Disclosures" || menuItem.label === "Disclosures";
    const isSubmitPage = menuItem.label === "Submit Loan";
    const isPricing = menuItem.label === "Product Pricing & Lock";
    const lockValidationGate =
        !skipModuleValidations &&
        isDisclosurePage &&
        lockNeedsValidation;

    // Rule 4: Business doc requirement (merged, not returned early)
    // let businessDocMissing = false;
    // if (menuItem.label !== "Product Pricing & Lock" && loanType === 'Business') {
    //     const businessDocGroup = validationConfig.FieldGroups.find(group => group.LoanType === 'Business');
    //     const businessDocField = businessDocGroup?.Fields?.find(f => f.document);
    //     const docTitle = businessDocField?.document;

    //     if (docTitle) {
    //         const docId = await checkDocumentByAssignedEntity(accessToken, loanId, docTitle);
    //         businessDocMissing = !docId; // mark true if missing
    //     }
    // }

    // Rule 5: Licensing gate (only when Send Disclosures / Disclosures) ---
    let licenseGate = false;

    if (!skipModuleValidations && (isDisclosurePage || isSubmitPage || isPricing)) {
        licenseGate = await runLicensingGateForMenu(
            loanType,
            stateCode,
            city,
            tpoOrgId,
            tpoUserId,
            accessToken,
            legalEntityType
        );
    }

    //Rule 6: Block Disclosure/Submit while Polly lock is processing
    const lockStatusRaw = values['LOCKRATE.CURRENTSTATUS'];

    const lockProcessingGate =
        !skipModuleValidations &&
        (isDisclosurePage || isSubmitPage) &&
        isPollyProcessingStatus(lockStatusRaw, values['1401'], loanType);

    // Rule 7: Dynamic Documentation vs Income validation (from JSON only if group exists)
    let docIncomeInvalid = false;
    const docIncomeGroup = validationConfig.FieldGroups.find(g => g.Group === "Documentation vs Income");
    if (docIncomeGroup) {
        const docType = (values["CX.DOC.TYPE"] || "").trim();
        const incomeType = (values["CX.INCOME.TYPE"] || "").trim();
        const isValidPair = evaluateCompositeCondition(docIncomeGroup.NotRequiredIf, values);
        if (!docType || !incomeType || !isValidPair) docIncomeInvalid = true;
    }

    // Rule 8: Processing Fee rules (Consumer only)
    // 1) BOTH empty => Warning gate (redirect to page, but not hardstop)
    // 2) BOTH filled => Conflict gate (hardstop)
    // 3) Exactly one filled => Do nothing
    const isRequestDisclosurePage = menuItem.label === "Request Disclosure";
    const isDisclosureOrRequest = isDisclosurePage || isRequestDisclosurePage;

    const inhouseEmpty = isEmptyField(values['1621']);
    const thirdPartyEmpty = isEmptyField(values['NEWHUD.X139']);

    const processingFeeBothEmpty = inhouseEmpty && thirdPartyEmpty;
    const processingFeeBothFilled = !inhouseEmpty && !thirdPartyEmpty;

    const processingFeeWarningGate =
        loanType === 'Consumer' &&
        isDisclosureOrRequest &&
        processingFeeBothEmpty;

    const processingFeeConflictGate =
        loanType === 'Consumer' &&
        isDisclosureOrRequest &&
        processingFeeBothFilled;

    // Rule 9: LO Comp not configured gate (Consumer + Lender Paid + LCP.X8 empty)
    // Applies to Send Disclosures OR Request Disclosure
    const loCompType = (values['LCP.X1'] || '').trim();
    const loCompBasePlanTotal = (values['LCP.X8'] || '').toString().trim();

    const loCompNotConfiguredGate =
        !skipModuleValidations &&
        loanType === 'Consumer' &&
        (isDisclosureOrRequest || isPricing) &&
        loCompType.localeCompare('Lender Paid', undefined, { sensitivity: 'accent' }) === 0 &&
        isFieldEffectivelyEmpty('LCP.X8', loCompBasePlanTotal);

    // Rule 10: Pricing Exception Approved gate
    // If program selected (1401 not empty) AND exception approved = Y
    // Block access to Product Pricing & Lock page

    const programSelected = !isEmptyField(values['1401']);
    const pricingExceptionApproved =
        (values['CX.EXC.PRE.ISAPPROVED'] || '')
            .toString()
            .trim()
            .toUpperCase() === 'Y';

    const pricingExceptionGate =
        !skipModuleValidations &&
        isPricing &&
        programSelected &&
        pricingExceptionApproved;

    // Rule 11: Closing Fee service order required
    // Consumer + Send Disclosures / Request Disclosure => CLOSINGFEES order must be fulfilled
    let closingFeeOrderGate = false;

    if (
        !skipModuleValidations && loanType === 'Consumer' && isDisclosureOrRequest
    ) {
        try {
            const serviceOrders = await getServiceOrders(refreshedToken, loanId);
            closingFeeOrderGate = !hasFulfilledClosingFeeOrder(serviceOrders);
        } catch (err) {
            console.error('[checkValidationRequired] Closing Fee service order check failed:', err);
            closingFeeOrderGate = true;
        }
    }

    console.log(
        '[checkValidationRequired flags]',
        {
            hasMissingField,
            hasInvalidByValidate,
            showIfTriggered,
            lockValidationGate,
            lockProcessingGate,
            licenseGate,
            docIncomeInvalid,
            hasDisclosureValidationRequired,
            processingFeeWarningGate,
            processingFeeConflictGate,
            loCompNotConfiguredGate,
            pricingExceptionGate,
            closingFeeOrderGate
        }
    );

    return hasMissingField ||
        hasInvalidByValidate ||
        showIfTriggered ||
        lockValidationGate ||
        lockProcessingGate ||
        licenseGate ||
        docIncomeInvalid ||
        hasDisclosureValidationRequired ||
        processingFeeWarningGate ||
        processingFeeConflictGate ||
        loCompNotConfiguredGate ||
        pricingExceptionGate ||
        closingFeeOrderGate;
};
